import React from "react";
import { makeStyles } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import { Button, Typography } from "@material-ui/core";
import clsx from "clsx";

interface MyPageButtonProps {
	disabled?: boolean | undefined;
	fullWidth?: boolean | undefined;
	className?: string | undefined;
	children?: any;
	backIcon?: "chevron" | string | boolean;
	nextIcon?: "chevron" | string | boolean;
	onClick?: () => void;
	style?: object;
	type?: "button" | "reset" | "submit";
	floatRight?: boolean;
	buttonVariant?: "text" | "contained" | "outlined";
	typoVariant?:
		| "inherit"
		| "button"
		| "overline"
		| "caption"
		| "h1"
		| "h2"
		| "h3"
		| "h4"
		| "h5"
		| "h6"
		| "subtitle1"
		| "subtitle2"
		| "body1"
		| "body2"
		| "srOnly"
		| undefined;
	actionType?: "save" | "cancel" | "delete" | "action" | "navigate";
	noFlex?: boolean;
}

const useStyles = makeStyles((theme) => ({
	MyPageButton: {
		textTransform: "none",
	},
	viewMoreLink: {
		color: "#05A",
	},
	flex: {
		display: "flex",
	},
	floatRight: {
		float: "right",
	},
	iconStyle: {
		fontSize: "1rem",
		paddingTop: 4,
	},
	saveButton: {
		backgroundColor: "#0B2",
		color: "#FFF",
	},
	cancelButton: {
		backgroundColor: "#FFF",
		color: "#05A",
		border: "1px solid #05A",
	},
	deleteButton: {
		backgroundColor: "#D00",
		color: "#FFF",
	},
	actionButton: {
		backgroundColor: "#05A",
		color: "#FFF",
	},
	navigateButton: {
		backgroundColor: theme.palette.secondary.main,
		color: "#000",
	},
	boldUnderline: {
		fontWeight: "bold",
		textDecoration: "underline",
	},
}));

const MyPageButton = (props: MyPageButtonProps) => {
	const classes = useStyles();

	const {
		disabled,
		fullWidth,
		className,
		children,
		nextIcon,
		backIcon,
		onClick,
		floatRight,
		typoVariant,
		buttonVariant,
		style,
		actionType,
		noFlex,
		type,
	} = props;

	const getBackIcon = (type: string | boolean | undefined) => {
		if (!type) {
			return null;
		}

		switch (type) {
			case "chevron":
				return <ChevronLeftIcon className={classes.iconStyle} />;
			default:
				return <ArrowBackIcon className={classes.iconStyle} />;
		}
	};

	const getNextIcon = (type: string | boolean | undefined) => {
		if (!type) {
			return null;
		}

		switch (type) {
			case "chevron":
				return <ChevronRightIcon className={classes.iconStyle} />;
			default:
				return <ArrowForwardIcon className={classes.iconStyle} />;
		}
	};

	return (
		<Button
			type={type ?? "button"}
			className={clsx(
				{
					[classes.floatRight]: floatRight,
					[classes.saveButton]: actionType === "save",
					[classes.cancelButton]: actionType === "cancel",
					[classes.deleteButton]: actionType === "delete",
					[classes.actionButton]: actionType === "action",
					[classes.navigateButton]: actionType === "navigate",
				},
				classes.MyPageButton,
				className
			)}
			onClick={() => onClick && onClick()}
			disabled={disabled}
			fullWidth={fullWidth}
			style={style}
			variant={buttonVariant}
		>
			<Typography
				variant={typoVariant || "subtitle2"}
				className={clsx(
					{
						[classes.viewMoreLink]: !disabled && !actionType,
						[classes.flex]: !noFlex,
					},
					classes.boldUnderline
				)}
			>
				{backIcon && getBackIcon(backIcon)}

				{children}
				{nextIcon && getNextIcon(nextIcon)}
			</Typography>
		</Button>
	);
};

export default MyPageButton;
